import React, { useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import DOMPurify from "dompurify";

const APP_STYLESHEET_PATTERN =
  /\/static\/|webpack|\/css\/main\.|\/css\/chunk|bootstrap|theme\.scss|app\.css|realtyspace/i;

const isFullHtmlDocument = (html) =>
  /<!doctype|<html[\s>]/i.test(html || "");

/** Keep landing page <head> styles; drop only app bundle stylesheets. */
export const prepareDocumentHtml = (html, stripAppStyles = true) => {
  if (!html || typeof html !== "string") return "";
  const doc = new DOMParser().parseFromString(html, "text/html");

  if (stripAppStyles) {
    doc.querySelectorAll("link[rel='stylesheet']").forEach((link) => {
      const href = link.getAttribute("href") || "";
      if (!href || APP_STYLESHEET_PATTERN.test(href)) {
        link.remove();
      }
    });
  }

  return `<!DOCTYPE html>\n${doc.documentElement.outerHTML}`;
};

export const stripAppStylesFromHtml = (html) => {
  if (!html || typeof html !== "string") return "";
  const doc = new DOMParser().parseFromString(html, "text/html");

  doc.querySelectorAll("link[rel='stylesheet']").forEach((link) => {
    const href = link.getAttribute("href") || "";
    if (!href || APP_STYLESHEET_PATTERN.test(href)) {
      link.remove();
    }
  });

  const headStyles = Array.from(
    doc.head.querySelectorAll("style, link[rel='stylesheet']")
  )
    .map((el) => el.outerHTML)
    .join("");
  const bodyContent = doc.body?.innerHTML || html;
  return headStyles ? `${headStyles}${bodyContent}` : bodyContent;
};

const prepareFragmentHtml = (html, stripAppStyles) => {
  if (!html) return "";
  const content = stripAppStyles ? stripAppStylesFromHtml(html) : html;
  return DOMPurify.sanitize(content, {
    USE_PROFILES: { html: true },
    ADD_TAGS: ["style"],
    ADD_ATTR: ["target", "rel"],
  });
};

// Ensure external/new-tab links stay safe after allowing target
if (typeof window !== "undefined" && !DOMPurify.__rsTargetHookAdded) {
  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName === "A" && node.getAttribute("target") === "_blank") {
      const existingRel = (node.getAttribute("rel") || "").toLowerCase();
      const parts = new Set(existingRel.split(/\s+/).filter(Boolean));
      parts.add("noopener");
      parts.add("noreferrer");
      node.setAttribute("rel", Array.from(parts).join(" "));
    }
  });
  DOMPurify.__rsTargetHookAdded = true;
}

function DocumentFrame({ html, fillHeight = false }) {
  const iframeRef = useRef(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || fillHeight) return;

    const resize = () => {
      try {
        const doc = iframe.contentDocument;
        if (!doc) return;
        const height = Math.max(
          doc.documentElement?.scrollHeight || 0,
          doc.body?.scrollHeight || 0
        );
        iframe.style.height = `${height}px`;
      } catch (_) {
        /* cross-origin guard */
      }
    };

    iframe.addEventListener("load", resize);
    resize();
    return () => iframe.removeEventListener("load", resize);
  }, [html, fillHeight]);

  return (
    <iframe
      ref={iframeRef}
      title="HTML preview"
      srcDoc={html}
      className={
        fillHeight
          ? "safe-html-document-frame safe-html-document-frame--fill"
          : "safe-html-document-frame"
      }
      style={{
        width: "100%",
        border: "none",
        display: "block",
        minHeight: fillHeight ? "100%" : 400,
        height: fillHeight ? "100%" : undefined,
      }}
      sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
    />
  );
}

DocumentFrame.propTypes = {
  html: PropTypes.string.isRequired,
  fillHeight: PropTypes.bool,
};

function ShadowHtml({ html }) {
  const hostRef = useRef(null);

  useEffect(() => {
    if (!hostRef.current) return;
    const host = hostRef.current;
    let shadow = host.shadowRoot;
    if (!shadow) {
      shadow = host.attachShadow({ mode: "open" });
    }
    shadow.innerHTML = html;
  }, [html]);

  return <div ref={hostRef} className="safe-html-isolate" />;
}

ShadowHtml.propTypes = {
  html: PropTypes.string.isRequired,
};

export default function SafeHTML({
  html,
  isolate = false,
  stripAppStyles = false,
  renderAsDocument,
  fillHeight = false,
}) {
  const useDocument =
    renderAsDocument ?? (isolate && isFullHtmlDocument(html));

  const documentHtml = useMemo(
    () => (useDocument ? prepareDocumentHtml(html, stripAppStyles) : ""),
    [html, stripAppStyles, useDocument]
  );

  const fragmentHtml = useMemo(
    () => (!useDocument ? prepareFragmentHtml(html, stripAppStyles) : ""),
    [html, stripAppStyles, useDocument]
  );

  if (!html) return null;

  if (useDocument) {
    return <DocumentFrame html={documentHtml} fillHeight={fillHeight} />;
  }

  if (isolate) {
    return <ShadowHtml html={fragmentHtml} />;
  }

  return (
    <div
      className="safe-html-content"
      dangerouslySetInnerHTML={{ __html: fragmentHtml }}
    />
  );
}

SafeHTML.propTypes = {
  html: PropTypes.string,
  isolate: PropTypes.bool,
  stripAppStyles: PropTypes.bool,
  renderAsDocument: PropTypes.bool,
  fillHeight: PropTypes.bool,
};
